import Grid from '@mui/material/Grid';
import * as NextRouter from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import ItemListingCard from './ItemListingCard';
import { SLMainBoxContainer } from './ItemListing.style';
import {
  STCRWrapper,
  StCRGridContainerWithoutBackground,
} from '../shared/Container/Container.styles';
import {
  ItemListingContentType,
  ItemListingProps,
  ItemListingSitecoreItem,
} from './ItemListing.type';
import { mapItemListingCards, resolveItemListingContentType } from './mapItemListingCard';
import FilterChips from '../shared/FilterChips/FilterChips';
import { ALL_ARTICLES_LABEL, ALL_STUDIES_LABEL } from '../shared/FilterChips/FilterChips.types';
import {
  getArticleConditionLabel,
  getArticleNavigateHref,
  getStudyConditionLabel,
  getStudyNavigateHref,
} from '../shared/FilterChips/FilterChips.utils';

function getLimitValue(field?: { value?: number | string }, fallback = 0): number {
  const raw = field?.value;
  if (raw === undefined || raw === null || raw === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getCurrentPath(asPath?: string, pathQuery?: string | string[]): string {
  if (asPath) return asPath;
  if (Array.isArray(pathQuery)) return `/${pathQuery.join('/')}`;
  if (typeof pathQuery === 'string' && pathQuery) return `/${pathQuery}`;
  if (typeof window !== 'undefined') return window.location.pathname || '';
  return '';
}

function ItemListing(props: ItemListingProps): JSX.Element {
  const router = NextRouter.useRouter();
  const fields = props.fields;
  const listParam = props.params?.list;
  const studyList = fields?.studyList || [];
  const articleList = fields?.articleList || [];
  // itemList is retained on the Sitecore template for future pages; not rendered yet.
  const currentPath = getCurrentPath(router.asPath, router.query?.path);
  const contentType = resolveItemListingContentType(currentPath, listParam);
  const showFilterChips = Boolean(fields?.enableFilterChip?.value);

  const sourceList = useMemo((): ItemListingSitecoreItem[] => {
    if (contentType === ItemListingContentType.Study) return studyList;
    if (contentType === ItemListingContentType.Article) return articleList;
    return [];
  }, [contentType, studyList, articleList]);

  const [filteredRawList, setFilteredRawList] = useState<ItemListingSitecoreItem[]>(sourceList);

  useEffect(() => {
    setFilteredRawList(sourceList);
  }, [sourceList]);

  const cards = useMemo(() => {
    if (!contentType) return [];
    return mapItemListingCards(filteredRawList, contentType);
  }, [contentType, filteredRawList]);

  if (!fields) return <></>;

  const isStudyListing = contentType === ItemListingContentType.Study;
  const isArticleListing = contentType === ItemListingContentType.Article;

  return (
    <SLMainBoxContainer>
      <STCRWrapper>
        <StCRGridContainerWithoutBackground sx={{ marginBlock: '2rem' }}>
          {showFilterChips && isStudyListing && (
            <FilterChips
              items={studyList}
              onFilterChange={setFilteredRawList}
              getConditionLabel={getStudyConditionLabel}
              getNavigateHref={getStudyNavigateHref}
              allLabel={ALL_STUDIES_LABEL}
              align="start"
            />
          )}
          {showFilterChips && isArticleListing && (
            <FilterChips
              items={articleList}
              onFilterChange={setFilteredRawList}
              getConditionLabel={getArticleConditionLabel}
              getNavigateHref={getArticleNavigateHref}
              allLabel={ALL_ARTICLES_LABEL}
              align="start"
            />
          )}
          <Grid container rowGap={7} columnSpacing={6}>
            {cards.map((card) => (
              <Grid item xs={12} md={6} lg={4} key={card.id}>
                <ItemListingCard
                  card={card}
                  descriptionWordLimit={getLimitValue(fields.descriptionWordLimit, 30)}
                  titleCharacterLimit={getLimitValue(fields.titleCharacterLimit, 30)}
                  titleLineLimit={getLimitValue(fields.titleLineLimit, 2)}
                  descriptionLineLimit={getLimitValue(fields.descriptionLineLimit, 3)}
                />
              </Grid>
            ))}
          </Grid>
        </StCRGridContainerWithoutBackground>
      </STCRWrapper>
    </SLMainBoxContainer>
  );
}

export default ItemListing;
